import { FC, useCallback, useMemo } from 'react'
import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import cn from 'classnames'
import { Field } from '@/api/types/crm/crm.types'
import { useAddAlert } from '@/hooks/useAddAlert'
import { pickVkLinkField } from '@pages/Admin/CRM/utils/mainProfileFields'
import { isCrmLeadIntegrationField } from '@pages/Admin/CRM/utils/crmLeadIntegrationFields'
import {
  isVkIntegrationLeadProfileFieldsEnabled,
  type VkIntegrationLeadChatAvailability,
} from '@pages/Admin/CRM/utils/vkIntegrationLeadChat'
import LeadField from '../../../components/LeadField/LeadField'

import classes from './index.module.css'

const VK_LINK_TOOLTIP =
  'Формат: https://vk.com/username или числовой id'

type VkLinkFieldProps = {
  fields: Field[]
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  vkIntegration: VkIntegrationLeadChatAvailability & { isLoading?: boolean }
  className?: string
  error?: string
}

const VkLinkField: FC<VkLinkFieldProps> = ({
  fields,
  value,
  onChange,
  disabled = false,
  vkIntegration,
  className,
  error,
}) => {
  const addAlert = useAddAlert()

  const catalogField = useMemo(() => pickVkLinkField(fields), [fields])
  const isVisible = useMemo(
    () =>
      Boolean(catalogField) &&
      !isCrmLeadIntegrationField(catalogField) &&
      isVkIntegrationLeadProfileFieldsEnabled(vkIntegration),
    [catalogField, vkIntegration],
  )

  const trimmedValue = value.trim()

  const handleCopy = useCallback(async () => {
    if (!trimmedValue) return

    try {
      await navigator.clipboard.writeText(trimmedValue)
      addAlert({
        title: 'Успешно',
        text: 'Ссылка скопирована в буфер обмена',
        type: 'success',
      })
    } catch {
      addAlert({
        title: 'Ошибка',
        text: 'Не удалось скопировать ссылку',
        type: 'error',
      })
    }
  }, [addAlert, trimmedValue])

  if (!isVisible || !catalogField) return null

  return (
    <div className={cn(className)}>
      <div className={classes.block}>
        <div className={classes.header}>
          <span className={classes.label}>Ссылка ВК</span>
          <Tooltip
            placement="top"
            title={VK_LINK_TOOLTIP}
            overlayClassName={classes.tooltipOverlay}
          >
            <InfoCircleOutlined
              className={classes.infoIcon}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </Tooltip>
        </div>
        <div className={classes.field}>
          <div
            className={cn(classes.fieldControl, {
              [classes.fieldControlError]: Boolean(error),
            })}
          >
            <div className={classes.fieldInput}>
              <LeadField
                field={catalogField}
                size="small"
                value={value}
                onChange={onChange}
                disabled={disabled}
              />
            </div>
            {trimmedValue ? (
              <Tooltip title="Скопировать ссылку" placement="top">
                <button
                  type="button"
                  className={classes.copyButton}
                  aria-label="Скопировать ссылку ВК"
                  onClick={() => void handleCopy()}
                >
                  <CopyOutlined
                    className={classes.copyIcon}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                </button>
              </Tooltip>
            ) : null}
          </div>
          {error ? (
            <span className={cn(classes.status, classes.statusError)}>{error}</span>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default VkLinkField
